/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import {
  ACCRUED_DIVIDENDS_ROW_NUMBER,
  INVESTMENT_DATE_ROW_NUMBER,
  MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER,
  SHARES_OUTSTANDING_ROW_NUMBER,
} from 'common/constants/cap-table';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { LedgerTable } from 'components';
import { LedgerDialog } from 'components/Dialogs';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { cellsParser, reverseCellsParser } from 'components/LedgerTable/utilities/ledgerParser';
import CapTableContext from 'context/CapTableContext';
import { calcSharesOutstandingFromMultipleInvestments } from 'pages/CapTable/cap-table/utilities/parser';
import { useTableValidation } from 'services/hooks';
import { onCellsChanged as onLedgerCellsChanged } from 'utilities';
import { gridShortDate } from 'utilities/dateFormats';
import colConfig from './colConfig';
import MultipleInvestmentDataProps from './MultipleInvestmentData.props';
import { MultipleInvestmentDatesCell } from './types';

const emptyRow = {
  date: null,
  shares: null,
  accrued_dividends: null,
  isNew: true,
};

const MultipleInvestmentDates = ({ cell, closeDialog }: MultipleInvestmentDataProps) => {
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  const [rows, setRows] = useState<MultipleInvestmentDatesCell[]>([]);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [ledgerCells, setLedgerCells] = useState<Record<string, MultipleInvestmentDatesCell>>({});

  const { validateTable } = useTableValidation();
  const { cells, columns, onCellsChanged, format } = useContext(FeaturedSpreadsheetContext);
  const { isDisabled, measurementDate } = useContext(CapTableContext);

  const sourceKey = useMemo(() => cell.columnLegend + MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER, []);
  const investmentDateKey = useMemo(() => cell.columnLegend + INVESTMENT_DATE_ROW_NUMBER, []);
  const sharesOutstandingKey = useMemo(() => cell.columnLegend + SHARES_OUTSTANDING_ROW_NUMBER, []);
  const accruedDividendsKey = useMemo(() => cell.columnLegend + ACCRUED_DIVIDENDS_ROW_NUMBER, []);

  // Reserve the cells parse
  const investmentDates = useMemo(() => {
    if (!isEmpty(ledgerCells) && !isEmpty(rows)) {
      return reverseCellsParser(rows, ledgerCells);
    }
    return [];
  }, [rows, ledgerCells]);

  const config = useMemo(() => (isEmpty(captableInfo) ? [] : colConfig(measurementDate as string)), []);

  const addRow = () => {
    setRows([...investmentDates, emptyRow]);
  };

  const deleteRow = (rowIndex: number) => {
    const tmpRows = [...investmentDates];
    tmpRows.splice(rowIndex, 1);
    setRows(tmpRows);
  };

  const save = () => {
    const cellsToValidate = Object.entries(ledgerCells)
      .map(([, ledgerCell]) => ledgerCell)
      .filter(ledgerCell => ledgerCell.insideLedger);
    const { areCellsValid, validatedCells } = validateTable({ cellsToValidate, toggleAlert: false });

    if (areCellsValid) {
      const tmpState = { ...cells };
      const changes = [];

      // Find the earliest date to save it as the default Investment Date
      const earliestDate = investmentDates
        .map((investment: MultipleInvestmentDatesCell) => gridShortDate(investment.date))
        .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())
        .shift();

      // Reset "Investment Date" and "Shares Outstanding"
      if (isEmpty(investmentDates)) {
        // Reset "Investment Date"
        changes.push({
          cell: tmpState[investmentDateKey],
          value: gridShortDate(),
        });

        // Reset "Shares Outstanding"
        changes.push({
          cell: tmpState[sharesOutstandingKey],
          value: tmpState[sharesOutstandingKey].defaultValue,
        });
      }

      // Save shares outstanding and investment date
      if (investmentDates[0]) {
        changes.push({
          cell: tmpState[investmentDateKey],
          value: earliestDate,
        });

        // The shares outstanding (row 5), should be the sum of all the shares in the ledger
        const totalShares = calcSharesOutstandingFromMultipleInvestments(investmentDates);

        changes.push({
          cell: tmpState[sharesOutstandingKey],
          value: totalShares.toString(),
        });
      }

      // Ensure all investment dates have accrued_dividends calculated
      // The reverseCellsParser should have already extracted the calculated values,
      // but we ensure the structure is correct by mapping over rows with their indices
      const investmentDatesWithAccruedDividends = investmentDates.map((investment: MultipleInvestmentDatesCell, index: number) => {
        // Ensure accrued_dividends is included (should already be from reverseCellsParser)
        // If the accrued dividends is missing, get it from the ledger cells
        const accruedDividendsCellKey = `accrued_dividends${index + 1}`;
        const accruedDividendsCell = ledgerCells[accruedDividendsCellKey];
        const accruedDividendsValue = investment.accrued_dividends ?? (accruedDividendsCell?.value ?? null);
        
        return {
          ...investment,
          accrued_dividends: accruedDividendsValue,
        };
      });

      changes.push({
        cell: tmpState[sourceKey],
        value: investmentDatesWithAccruedDividends,
      });

      onCellsChanged(changes);
      closeDialog();
    } else {
      setIsAlertVisible(true);
      // Replace current cells with the validated ones
      setLedgerCells(validatedCells.reduce((acc, curr) => ({ ...acc, [curr.key!]: curr }), {}) as Record<string, MultipleInvestmentDatesCell>);
    }
  };

  const getInitialData = () => {
    const cellColumnIndex = cell.columnOrder;
    const findMultipleInvestments = (columns as unknown as Array<{ order: number; multiple_investments?: MultipleInvestmentDatesCell[] }>).find(
      column => column.order === cellColumnIndex
    )?.multiple_investments;

    const valueDate = cells[investmentDateKey].value;
    let ledgerData = [...(cells[sourceKey].value as MultipleInvestmentDatesCell[])];

    // Use the security values to create the first row
    if (isEmpty(ledgerData)) {
      ledgerData = findMultipleInvestments ?? [];
    }

    if (isEmpty(findMultipleInvestments)) {
      const payload = {
        date: valueDate ? gridShortDate(valueDate as string) : null,
        shares: cells[sharesOutstandingKey].value as string,
        accrued_dividends: cells[accruedDividendsKey].value as string,
      } as unknown as Partial<MultipleInvestmentDatesCell>;

      ledgerData.unshift(payload as unknown as MultipleInvestmentDatesCell);
    }

    setRows(ledgerData);
  };

  useEffect(() => {
    if (cells && sourceKey && cells[sourceKey]) {
      getInitialData();
    }
  }, [cells, sourceKey]);

  useEffect(() => {
    if (!isNil(rows)) {
      const parsedCells = cellsParser(rows, config, cell);
      const changes = rows.flatMap((_, index) => {
        const rowChanges = [];
        const sharesCell = parsedCells[`shares${index + 1}`];
        const accruedDividendsCell = parsedCells[`accrued_dividends${index + 1}`];
        
        // Ensure shares cell has its expression set
        if (sharesCell) {
          rowChanges.push({
            cell: sharesCell,
            value: sharesCell.expr || sharesCell.value,
          });
        }
        
        // Ensure accrued_dividends cell has its expression evaluated
        if (accruedDividendsCell?.expr) {
          rowChanges.push({
            cell: accruedDividendsCell,
            value: accruedDividendsCell.expr,
          });
        }
        
        return rowChanges;
      });
      onLedgerCellsChanged(changes, { ...parsedCells, ...cells }, {}).then(updatedCells => {
        setLedgerCells(updatedCells as Record<string, MultipleInvestmentDatesCell>);
      });
    }
  }, [rows]);

  return (
    <LedgerDialog
      id="multiple-investment-dates-ledger"
      title="Multiple Investment Dates"
      onSave={save}
      onClose={closeDialog}
      disabled={cell.isDisabled}>
      {ledgerCells && (
        <LedgerTable
          cell={cell}
          colConfig={config}
          rows={rows}
          disabled={isDisabled}
          tableTerms={{
            tableName: 'Multiple Investment Dates',
            tableSlug: 'multiple-investment-dates-ledger',
            columnName: 'Investment Date',
            pluralColumnName: 'Investment Dates',
          }}
          isAlertVisible={isAlertVisible}
          setIsAlertVisible={setIsAlertVisible}
          addRow={addRow}
          deleteRow={deleteRow}
          cells={ledgerCells}
          sharedCells={cells}
          setCells={setLedgerCells}
          currency={format.currency}
          showTotalRow
        />
      )}
    </LedgerDialog>
  );
};

export default MultipleInvestmentDates;
